import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSession,
  getSession,
  type ChartSpec,
  type ChatMessage,
  saveAsReport,
  sendMessage,
} from "@/lib/chat-api";
import { ChatChart } from "@/components/chat-chart";
import { ChatResultTable } from "@/components/chat-result-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface LatestResult {
  rows: Record<string, unknown>[] | null;
  chart: ChartSpec | null;
  sql: string | null;
}

export default function ChatPage() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [latest, setLatest] = useState<LatestResult>({
    rows: null,
    chart: null,
    sql: null,
  });
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sessionId) return;
    createSession().then(({ session }) => setSessionId(session.id));
  }, [sessionId]);

  const { data: sessionData } = useQuery({
    queryKey: ["chat-session", sessionId],
    queryFn: () => (sessionId ? getSession(sessionId) : Promise.resolve(null)),
    enabled: !!sessionId,
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => {
      if (!sessionId) throw new Error("No session");
      return sendMessage(sessionId, content);
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["chat-session", sessionId] });
      setLatest({ rows: resp.rows, chart: resp.chart, sql: resp.message.sql });
      setInput("");
    },
  });

  const saveMutation = useMutation({
    mutationFn: (args: { name: string; sql: string; chart?: ChartSpec }) =>
      saveAsReport({
        name: args.name,
        sql: args.sql,
        chartConfig: args.chart,
      }),
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sessionData?.messages?.length, sendMutation.isPending]);

  const messages = sessionData?.messages ?? [];

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      <h1 className="text-xl font-semibold">Chat with your data</h1>

      <div className="flex-1 overflow-auto border rounded-md p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-sm text-muted-foreground">
            Ask a question, e.g. "How many boiler service jobs were completed this month by engineer?"
          </div>
        )}
        {messages.map((m: ChatMessage) => (
          <div key={m.id} className={m.role === "user" ? "text-right" : ""}>
            <div
              className={`inline-block max-w-prose rounded-lg px-3 py-2 text-sm ${
                m.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted"
              }`}
            >
              {m.content}
            </div>
            {m.role === "assistant" && m.sql && (
              <details className="text-xs text-muted-foreground mt-1 ml-1">
                <summary className="cursor-pointer">Show SQL</summary>
                <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-auto">
                  {m.sql}
                </pre>
              </details>
            )}
          </div>
        ))}
        {sendMutation.isPending && (
          <div className="text-sm text-muted-foreground">Thinking…</div>
        )}
        {sendMutation.isError && (
          <div className="text-sm text-red-600">
            {String(sendMutation.error)}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {latest.rows && <ChatResultTable rows={latest.rows} />}
      {latest.chart && latest.rows && (
        <ChatChart spec={latest.chart} rows={latest.rows} />
      )}

      {latest.sql && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const name = prompt("Report name?");
              if (!name || !latest.sql) return;
              saveMutation.mutate({
                name,
                sql: latest.sql,
                chart: latest.chart ?? undefined,
              });
            }}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving…" : "Save as report"}
          </Button>
        </div>
      )}

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!input.trim()) return;
          sendMutation.mutate(input.trim());
        }}
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question about your data…"
          disabled={sendMutation.isPending}
        />
        <Button type="submit" disabled={sendMutation.isPending || !input.trim()}>
          Send
        </Button>
      </form>
    </div>
  );
}
